import { getByID } from '@adornis/baseql/operations/mongo';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration';
import { Base } from '@adornis/buildify/db/Base';
import type { CustomContent } from '@adornis/buildify/db/CustomContent';
import { PageStatus } from '@adornis/buildify/db/enums';
import { acss } from '@adornis/chemistry/directives/acss';
import { css } from '@adornis/chemistry/directives/css';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar';
import { DesignSystem } from '@adornis/chemistry/elements/theming/design';
import { XDialog } from '@adornis/dialog/x-dialog';
import { useForm } from '@adornis/forms/use-form';
import { stateful } from '@adornis/functional-lit/stateful';
import { useQuery } from '@adornis/functional-lit/use-query';
import { makeRouter } from '@adornis/router/client/make-router';
import { html, type TemplateResult } from 'lit';
import type { DirectiveResult } from 'lit/async-directive';
import { firstValueFrom, timer } from 'rxjs';
import { CustomContentsList } from './CustomContentsList';
import { transformStatus } from './TransformStatus';

export const makeCustomContentsRouter = ({
  customContentsClass,
  buildifyEditor,
}: {
  customContentsClass: typeof CustomContent;
  buildifyEditor: (customContent: CustomContent) => TemplateResult | DirectiveResult;
}) => {
  const router = makeRouter(
    {
      edit: {
        path: '/:id',
        render: stateful(params => {
          const customContentID = params.id;
          if (!customContentID) return html`<d-text>No custom content ID</d-text>`;

          const { value: customContent, loading: isCustomContentLoading } = useQuery(
            () =>
              getByID(
                customContentsClass,
                customContentID,
              )({ name: 1, status: 1, isTemplate: 1, content: selectionSet(() => Base, 8) }),
            [customContentID],
          );
          if (isCustomContentLoading) return html`<d-loader>Lädt...</d-loader>`;
          if (!customContent) return html`<d-text>Kein Inhalt gefunden</d-text>`;

          const form = useForm(customContent);
          const design = DesignSystem.currentTheme.getValue();

          return html`
            <d-flex horizontal crossaxis-center space-between>
              <d-flex
                horizontal
                crossaxis-center
                space="sm"
                ${css({ cursor: 'pointer', userSelect: 'none' })}
                @click=${() => router.goBack('hierarchy')}
              >
                <d-icon ${css({ color: design.colors.accent })}> arrow-left </d-icon>
                <d-text
                  ${acss({
                    '&:hover': {
                      color: design.colors.accent,
                    },
                  })}
                >
                  Zurück
                </d-text>
              </d-flex>
              <d-button
                @click=${() => {
                  XDialog.waitFor(
                    Promise.all([firstValueFrom(timer(1000)), customContent.save()]),
                    'Wird gespeichert...',
                  ).then(() => XSnackbar.show('Gespeichert!'));
                }}
              >
                Speichern
              </d-button>
            </d-flex>
            <d-flex space="xs">
              <d-checkbox ${form.field('isTemplate')} .label=${'Ist ein Template'}></d-checkbox>
              <d-input ${form.field('name')} placeholder="Name"></d-input>
              <d-dropdown-selection
                placeholder="Status"
                .selectables=${Object.values(PageStatus)}
                ${form.field('status')}
                .renderString=${key => transformStatus(key)}
              >
              </d-dropdown-selection>
            </d-flex>
            ${buildifyEditor(customContent as CustomContent)}
          `;
        }),
      },
    },
    {
      fallback: params => CustomContentsList.render({ customContentsClass, router }),
    },
  );
  return router;
};
